import React from 'react';
import {
  IngredientEditor,
  type IngredientEditorRef,
} from './ingredient-editor/ingredient-editor';
import { StepsEditor, type StepsEditorRef } from './steps-editor/steps-editor';
import { type RecipeFixture } from './fixtures/recipes';
import { ingredientsTextToHTML } from './fixtures/content-utils';

export interface ResizableRecipeFormProps {
  initialLeftWidth?: number; // percentage, default 40
  debug?: boolean;
  recipe?: RecipeFixture;
  appliances?: readonly string[];
}

export interface ResizableRecipeFormRef {
  getIngredientHTML: () => string;
  getStepsHTML: () => string;
  getIngredientNames: () => string[];
  getSteps: () => string[];
}

export const ResizableRecipeForm = React.forwardRef<
  ResizableRecipeFormRef,
  ResizableRecipeFormProps
>(({ initialLeftWidth = 40, debug = false, recipe, appliances }, ref) => {
  const ingredientEditorRef = React.useRef<IngredientEditorRef>(null);
  const stepsEditorRef = React.useRef<StepsEditorRef>(null);
  const [ingredientNames, setIngredientNames] = React.useState<string[]>(
    recipe?.ingredientNames || [],
  );
  const [leftWidth, setLeftWidth] = React.useState(initialLeftWidth);
  const [isDragging, setIsDragging] = React.useState(false);
  const [focusedEditor, setFocusedEditor] = React.useState<
    'ingredients' | 'steps' | null
  >(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Function to update ingredient names immediately
  const updateIngredientNames = React.useCallback(() => {
    const names = ingredientEditorRef.current?.extractIngredientNames() || [];

    // Clean and normalize extracted names
    const cleanedNames = names
      .map((name) =>
        name
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, '') // Remove special chars except spaces and hyphens
          .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
          .trim(),
      )
      .filter((name) => name.length > 0);

    // Update with cleaned ingredient names, removing duplicates
    const allIngredients = [...new Set(cleanedNames)];
    setIngredientNames(allIngredients);
  }, []);

  // Update ingredient names when steps editor becomes active
  const handleSetFocusedEditor = React.useCallback(
    (editor: 'ingredients' | 'steps' | null) => {
      if (editor === 'steps' && focusedEditor !== 'steps') {
        // Only extract ingredients when switching TO steps editor (not already focused)
        updateIngredientNames();
      }
      setFocusedEditor(editor);
    },
    [updateIngredientNames, focusedEditor],
  );

  // Update ingredient names only when ingredients editor is focused and editing
  React.useEffect(() => {
    if (focusedEditor === 'ingredients') {
      // Only update periodically when ingredients editor is active
      const interval = window.setInterval(updateIngredientNames, 3000);
      return () => window.clearInterval(interval);
    }
  }, [focusedEditor, updateIngredientNames]);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 20% and 80%, with minimum 300px for ingredient editor
      const minWidthPercent = Math.max(20, (300 / containerRect.width) * 100);
      const constrainedWidth = Math.min(
        Math.max(minWidthPercent, newLeftWidth),
        80,
      );

      setLeftWidth(constrainedWidth);
    },
    [isDragging],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useImperativeHandle(ref, () => ({
    getIngredientHTML: () => ingredientEditorRef.current?.getHTML() || '',
    getStepsHTML: () => stepsEditorRef.current?.getHTML() || '',
    getIngredientNames: () => ingredientNames,
    getSteps: () => stepsEditorRef.current?.getSteps() || [],
  }));

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative"
    >
      {/* Ingredients Panel */}
      <div
        className={`
          flex-shrink-0 overflow-hidden transition-all duration-200 ease-in-out
          ${focusedEditor === 'steps' ? 'opacity-40' : 'opacity-100'}
          ${focusedEditor === 'ingredients' ? 'ring-2 ring-emerald-400 dark:ring-emerald-500' : ''}
        `}
        style={{ width: `${leftWidth}%` }}
        onClick={() => handleSetFocusedEditor('ingredients')}
      >
        <div className="h-full overflow-auto">
          <div className="h-full">
            <IngredientEditor
              ref={ingredientEditorRef}
              placeholder="Add your recipe ingredients..."
              editable={focusedEditor !== 'steps'}
              debug={debug}
              initialContent={
                recipe
                  ? ingredientsTextToHTML(recipe.ingredientsText)
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* Resizer Handle */}
      <div
        className={`
          relative w-2 cursor-col-resize transition-all duration-150 ease-in-out
          bg-gradient-to-b from-transparent via-slate-300 to-transparent
          dark:via-slate-600 hover:via-blue-400 dark:hover:via-blue-500
          ${isDragging ? 'via-blue-500 dark:via-blue-400 w-3' : 'hover:w-3'}
          group
        `}
        onMouseDown={handleMouseDown}
        data-testid="resize-handle"
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-12 flex items-center justify-center">
          <div className="flex flex-col gap-1">
            <div className="w-1 h-2 bg-slate-400 dark:bg-slate-500 group-hover:bg-blue-500 dark:group-hover:bg-blue-400 rounded-full transition-colors"></div>
            <div className="w-1 h-2 bg-slate-400 dark:bg-slate-500 group-hover:bg-blue-500 dark:group-hover:bg-blue-400 rounded-full transition-colors"></div>
            <div className="w-1 h-2 bg-slate-400 dark:bg-slate-500 group-hover:bg-blue-500 dark:group-hover:bg-blue-400 rounded-full transition-colors"></div>
          </div>
        </div>
      </div>

      {/* Steps Panel */}
      <div
        className={`
          flex-1 overflow-hidden transition-all duration-200 ease-in-out
          ${focusedEditor === 'ingredients' ? 'opacity-40' : 'opacity-100'}
          ${focusedEditor === 'steps' ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''}
        `}
        onClick={() => handleSetFocusedEditor('steps')}
      >
        <div className="h-full overflow-auto">
          <div className="h-full">
            <StepsEditor
              ref={stepsEditorRef}
              ingredientNames={ingredientNames}
              appliances={appliances}
              editable={focusedEditor !== 'ingredients'}
              debug={debug}
              initialContent={recipe?.stepsHTML}
            />
          </div>
        </div>
      </div>

      {/* Clear focus overlay */}
      {focusedEditor && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          onClick={(e) => {
            e.stopPropagation();
            handleSetFocusedEditor(null);
          }}
        />
      )}
    </div>
  );
});

ResizableRecipeForm.displayName = 'ResizableRecipeForm';
